import { DiscordSDK } from '@discord/embedded-app-sdk';

export type AuthedUser = {
  userId: string;
  name: string;
  avatar: string;
  /** True iff Discord SDK was actually initialized; false in dev/fake mode. */
  fromDiscord: boolean;
  /** Lobby key — instance_id from Discord, or a synthetic id in dev. */
  lobbyId: string;
};

const params = new URLSearchParams(window.location.search);

/** Backend base for /api calls. Discord routes everything through /.proxy/. */
export const API_BASE = isInsideDiscord() ? '/.proxy/api' : '/api';
export const WS_PATH = isInsideDiscord() ? '/.proxy/ws' : '/ws';

export function isInsideDiscord(): boolean {
  // Discord injects these query params into the iframe URL.
  return params.has('frame_id') && params.has('instance_id');
}

export function isDevMode(): boolean {
  return params.get('dev') === '1' || (!isInsideDiscord() && import.meta.env.DEV);
}

function fakeUser(): AuthedUser {
  const stash = (() => {
    try {
      const v = localStorage.getItem('geoguess.devUser');
      return v ? (JSON.parse(v) as { userId: string; name: string }) : null;
    } catch {
      return null;
    }
  })();
  const userId = stash?.userId ?? `dev-${Math.random().toString(36).slice(2, 10)}`;
  const adjectives = ['Salty', 'Stoic', 'Foggy', 'Loud', 'Tiny', 'Brave', 'Crisp', 'Wonky'];
  const animals = ['Otter', 'Crow', 'Lynx', 'Whale', 'Heron', 'Toad', 'Boar', 'Yak'];
  const name =
    stash?.name ??
    `${adjectives[Math.floor(Math.random() * adjectives.length)]} ${animals[Math.floor(Math.random() * animals.length)]}`;
  const lobbyId = params.get('lobby') ?? 'dev-lobby';
  try {
    localStorage.setItem('geoguess.devUser', JSON.stringify({ userId, name }));
  } catch {
    /* ignore */
  }
  return {
    userId,
    name,
    avatar: '',
    fromDiscord: false,
    lobbyId,
  };
}

const DISCORD_CLIENT_ID =
  (import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined) ?? '';

export async function initDiscord(): Promise<AuthedUser> {
  if (isDevMode()) return fakeUser();
  if (!isInsideDiscord()) return fakeUser();

  if (!DISCORD_CLIENT_ID) {
    // Inside Discord but the build didn't get a client id — bail to fake-user so
    // the UI is still usable rather than crashing.
    // eslint-disable-next-line no-console
    console.warn('[discord] VITE_DISCORD_CLIENT_ID missing — using fake user');
    return fakeUser();
  }

  const sdk = new DiscordSDK(DISCORD_CLIENT_ID);
  await sdk.ready();

  const { code } = await sdk.commands.authorize({
    client_id: DISCORD_CLIENT_ID,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: ['identify', 'guilds.members.read'],
  });

  const tokenRes = await fetch(`${API_BASE}/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!tokenRes.ok) {
    throw new Error(`auth_exchange_failed: ${tokenRes.status}`);
  }
  const { access_token } = (await tokenRes.json()) as { access_token: string };

  const auth = await sdk.commands.authenticate({ access_token });
  if (!auth?.user) throw new Error('discord_authenticate_no_user');

  const u = auth.user;
  const avatar = u.avatar
    ? `https://cdn.discordapp.com/avatars/${u.id}/${u.avatar}.png?size=128`
    : `https://cdn.discordapp.com/embed/avatars/${(Number(u.id) >> 22) % 6}.png`;

  // Lobby key = guild + channel so every voice channel on every server has its
  // own persistent room. Falls back to channel-only for DM activities (no guild)
  // and to instance_id only as a last resort if neither is available.
  const lobbyId = sdk.channelId
    ? `g${sdk.guildId ?? 'dm'}-c${sdk.channelId}`
    : sdk.instanceId;

  return {
    userId: u.id,
    name: u.global_name ?? u.username ?? 'Player',
    avatar,
    fromDiscord: true,
    lobbyId,
  };
}
